import React, { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import {
    AvailableWidgetCoordinates,
    getAvailableWidgetCoordinates,
    getCoordinatesPreview,
    getGridCellFromPointerPosition,
    NewWidgetType,
    WidgetCoordinates,
} from 'adapter/in/dashboards/components/Grid/newWidgetOverlayUtils';
import { CELL_MARGIN } from 'adapter/in/dashboards/components/Grid/constants';
import { NewWidgetOverlayAction, NewWidgetOverlayBox, NewWidgetOverlaySurface } from 'adapter/in/dashboards/components/Grid/style';
import { AddWidgetDropdown } from 'adapter/in/reports/components/AddWidgetDropdown/AddWidgetDropdown';
import { SaveReportTemplateLayoutUseCase } from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';
import { BusinessInsight } from 'domain/entity/feature';

type NewWidgetOverlayProps = {
    children: ReactNode;
    cellSize: { width: number; height: number };
    isEnabled: boolean;
    layout: GridLayoutElement[];
    reportTemplateId: string;
    contextPeriod?: string;
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase;
    onCoordinatesHoverChange?: (hasCoordinates: boolean) => void;
    onTextCoordinatesSelected?: (coordinates: WidgetCoordinates) => void;
    onBusinessInsightCoordinatesSelected?: (
        coordinates: WidgetCoordinates,
        businessInsight: BusinessInsight,
        businessInsightId: string
    ) => void;
};

const getCoordinatesBoxStyle = (coordinates: WidgetCoordinates, cellSize: { width: number; height: number }) => ({
    left: coordinates.x * (cellSize.width + CELL_MARGIN),
    top: coordinates.y * (cellSize.height + CELL_MARGIN),
    width: coordinates.w * cellSize.width + (coordinates.w - 1) * CELL_MARGIN,
    height: coordinates.h * cellSize.height + (coordinates.h - 1) * CELL_MARGIN,
});

const isPointerInsideCoordinatesPreview = (
    coordinates: WidgetCoordinates,
    pointerX: number,
    pointerY: number,
    cellSize: { width: number; height: number }
): boolean => {
    const style = getCoordinatesBoxStyle(coordinates, cellSize);

    return pointerX >= style.left && pointerX <= style.left + style.width && pointerY >= style.top && pointerY <= style.top + style.height;
};

export const NewWidgetOverlay = (props: NewWidgetOverlayProps): ReactElement => {
    const {
        children,
        cellSize,
        isEnabled,
        layout,
        reportTemplateId,
        contextPeriod,
        updateTemplateLayout,
        onCoordinatesHoverChange,
        onTextCoordinatesSelected,
        onBusinessInsightCoordinatesSelected,
    } = props;
    const [availableCoordinates, setAvailableCoordinates] = useState<AvailableWidgetCoordinates[]>([]);
    const [isCoordinatesMenuOpen, setIsCoordinatesMenuOpen] = useState(false);
    const coordinatesPreviewRef = useRef<HTMLDivElement>(null);

    const coordinatesPreview = useMemo(() => getCoordinatesPreview(availableCoordinates), [availableCoordinates]);
    const analysisCoordinates = availableCoordinates.find((coordinates) => coordinates.type === NewWidgetType.ANALYSIS)?.coordinates;

    useEffect(() => {
        if (!isEnabled && availableCoordinates.length > 0) {
            setAvailableCoordinates([]);
            onCoordinatesHoverChange?.(false);
        }
        if (!isEnabled && isCoordinatesMenuOpen) {
            setIsCoordinatesMenuOpen(false);
        }
    }, [availableCoordinates.length, isCoordinatesMenuOpen, isEnabled, onCoordinatesHoverChange]);

    useEffect(() => {
        if (!isCoordinatesMenuOpen) return undefined;

        const closeCoordinatesMenu = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (coordinatesPreviewRef.current?.contains(target)) return;
            if (target instanceof Element && target.closest('.ant-modal-wrap, .ant-modal-mask')) return;

            setIsCoordinatesMenuOpen(false);
        };

        document.addEventListener('mousedown', closeCoordinatesMenu, true);

        return () => {
            document.removeEventListener('mousedown', closeCoordinatesMenu, true);
        };
    }, [isCoordinatesMenuOpen]);

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isEnabled || isCoordinatesMenuOpen) return;

        const bounds = event.currentTarget.getBoundingClientRect();
        const pointerX = event.clientX - bounds.left;
        const pointerY = event.clientY - bounds.top;

        if (coordinatesPreview && isPointerInsideCoordinatesPreview(coordinatesPreview, pointerX, pointerY, cellSize)) return;

        const cell = getGridCellFromPointerPosition(pointerX, pointerY, cellSize);
        if (!cell) return;

        const nextCoordinates = getAvailableWidgetCoordinates(layout, cell);

        onCoordinatesHoverChange?.(nextCoordinates.length > 0);
        setAvailableCoordinates(nextCoordinates);
    };

    const handleMouseLeave = () => {
        if (isCoordinatesMenuOpen) return;

        onCoordinatesHoverChange?.(false);
        setAvailableCoordinates([]);
    };

    const keepCoordinatesPreviewOpen = () => setIsCoordinatesMenuOpen(true);

    const selectTextCoordinates = () => {
        const textCoordinates = availableCoordinates.find((coordinates) => coordinates.type === NewWidgetType.RICH_TEXT);
        if (!textCoordinates) return;

        onTextCoordinatesSelected?.(textCoordinates.coordinates);
        clearCoordinatesSelection();
    };

    const selectBusinessInsightCoordinates = (businessInsight: BusinessInsight, businessInsightId: string) => {
        const businessInsightCoordinates = availableCoordinates.find((coordinates) => coordinates.type === NewWidgetType.BUSINESS_INSIGHT);
        if (!businessInsightCoordinates) return;

        onBusinessInsightCoordinatesSelected?.(businessInsightCoordinates.coordinates, businessInsight, businessInsightId);
        clearCoordinatesSelection();
    };

    const clearCoordinatesSelection = () => {
        setAvailableCoordinates([]);
        setIsCoordinatesMenuOpen(false);
        onCoordinatesHoverChange?.(false);
    };

    return (
        <NewWidgetOverlaySurface
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            data-testid='report-grid-new-widget-surface'
        >
            {children}
            {isEnabled && coordinatesPreview && (
                <NewWidgetOverlayBox
                    ref={coordinatesPreviewRef}
                    data-testid='report-grid-new-widget-overlay'
                    style={getCoordinatesBoxStyle(coordinatesPreview, cellSize)}
                    data-available-widget-types={availableCoordinates.map((coordinates) => coordinates.type).join(',')}
                >
                    <NewWidgetOverlayAction onMouseDown={keepCoordinatesPreviewOpen} onClick={keepCoordinatesPreviewOpen}>
                        <AddWidgetDropdown
                            reportTemplateId={reportTemplateId}
                            isLayoutOverlay
                            contextPeriod={contextPeriod}
                            updateTemplateLayout={updateTemplateLayout}
                            analysisCoordinates={analysisCoordinates}
                            onTextWidgetSelected={selectTextCoordinates}
                            onBusinessInsightWidgetSelected={selectBusinessInsightCoordinates}
                        />
                    </NewWidgetOverlayAction>
                </NewWidgetOverlayBox>
            )}
        </NewWidgetOverlaySurface>
    );
};
